"use client";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAppContext } from "../../provider/AppProvider";
import gsap from "gsap";
import CustomEase from "gsap/CustomEase";
import { stegaClean } from "@sanity/client/stega";

import { NavigationData } from "../../../sanity/lib/queries/getNavigationData";

import LanguageSwitcher from "../languageSwitcher/languageSwitcher";
import { SocialLinks } from "./socialLinks";
import { ContactData } from "./contactData";
import { ContactLink } from "./contactLink";
import useIsMobile from "../../hooks/useIsMobile";
import { LocalizedLink } from "../link/localizedLink";

type Props = NavigationData;

const Nav = (data: Props) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [bgColor, setBgColor] = useState("#ffffff");
    const [index, setIndex] = useState(0);
    const [cleanedColors, setCleanedColors] = useState<string[]>([]);
    const menuRef = useRef(null);
    const isInitializedRef = useRef(false);
    const { setCursorState } = useAppContext();
    const { isMobile } = useIsMobile();
    const nav = useRef<HTMLDivElement>(null);
    const scrollContainer = useRef<HTMLDivElement>(null);

    const [scrollDirection, setScrollDirection] = useState<"up" | "down" | null>("up");
    const [changeDirectionY, setChangeDirectionY] = useState(0);
    const lastScrollY = useRef(0);

    const handleScroll = useCallback(() => {
        const currentScrollY = window.scrollY;

        if (currentScrollY < 30) {
            setScrollDirection("up");
            setChangeDirectionY(currentScrollY);
            return;
        }

        if (lastScrollY.current === 0 && currentScrollY > 30) {
            setScrollDirection("up");
            setChangeDirectionY(currentScrollY);
        } else {
            if (window.scrollY > 30) {
                if (
                    changeDirectionY + 100 < currentScrollY ||
                    changeDirectionY - 100 > currentScrollY
                ) {
                    if (currentScrollY > lastScrollY.current && currentScrollY > 30) {
                        setScrollDirection("down");
                    } else if (currentScrollY < lastScrollY.current) {
                        setScrollDirection("up");
                    }

                    setChangeDirectionY(currentScrollY);
                }
            }
        }

        lastScrollY.current = currentScrollY;
    }, [lastScrollY]);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        const colors = stegaClean(data.navigationBackgroundColors);

        setCleanedColors(colors);
    }, []);

    useLayoutEffect(() => {
        gsap.registerPlugin(CustomEase);
        CustomEase.create("hop", "M0,0 C0.354,0 0.464,0.133 0.498,0.502 0.532,0.872 0.651,1 1,1");
    }, []);

    useEffect(() => {
        if (menuRef.current && data.mainNavigation) {
            const menu = menuRef.current as HTMLElement;
            const links = menu.querySelectorAll(".menu-item");
            const and = menu.querySelectorAll(".menu-and");

            gsap.set(menu, {
                clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
            });

            gsap.set(links, { y: 30, opacity: 0 });
            gsap.set(and, { y: 30, opacity: 0 });

            isInitializedRef.current = true;
        }
    }, [data.mainNavigation]);

    const animateMenu = useCallback((open: boolean) => {
        // Toggle menu
        if (isAnimating) return; // Prevent multiple clicks during animation

        if (!menuRef.current) return;

        const menu = menuRef.current as HTMLElement;
        const links = menu.querySelectorAll(".menu-item");
        const and = menu.querySelectorAll(".menu-and");

        if (menu) {
            setIsAnimating(true);

            if (open) {
                gsap.to(menu, {
                    clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
                    ease: "hop",
                    duration: 1.5,
                    onStart: () => {
                        menu.style.pointerEvents = "all";
                    },
                    onComplete: () => {
                        setIsAnimating(false);
                    },
                });

                gsap.to(and, {
                    y: 0,
                    opacity: 1,
                    delay: 0.85,
                    duration: 1,
                    ease: "power3.out",
                });

                gsap.set(links, { y: "110%", opacity: 1 });

                gsap.to(links, {
                    y: 0,
                    stagger: 0.1,
                    delay: 1.05,
                    duration: 1,
                    ease: "power3.out",
                });
            } else {
                gsap.to(menu, {
                    clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
                    ease: "hop",
                    duration: 1.5,
                    onComplete: () => {
                        menu.style.pointerEvents = "none";

                        gsap.set(menu, {
                            clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
                        });

                        gsap.set(links, { y: 30, opacity: 0 });
                        gsap.set(and, { y: 30, opacity: 0 });

                        setIsAnimating(false);
                    },
                });
            }
        }
    }, []);

    const toggleMenu = useCallback(() => {
        if (!isAnimating) {
            if (!isMenuOpen) {
                setBgColor(cleanedColors[index]);
                setIndex((prevIndex) => {
                    return (prevIndex + 1) % cleanedColors.length;
                });
            }

            setIsMenuOpen((prevIsOpen) => {
                return !prevIsOpen;
            });
        }
    }, [isAnimating, isMenuOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsMenuOpen(false);
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    useEffect(() => {
        if (isInitializedRef.current) {
            animateMenu(isMenuOpen);
        }
    }, [isMenuOpen, animateMenu]);

    if (!data.mainNavigation) return;

    return (
        <header
            ref={nav}
            data-lenis-prevent="true"
            className={`md:bg-transparent nav sticky top-0 left-0 right-0 flex justify-between items-center w-full py-[11px] z-50 transition-all ease-in-out duration-300 ${isMobile && !isMenuOpen && scrollDirection === "down" ? "opacity-0 pointer-events-none" : "opacity-100 bg-[var(--bg-color)]"}`}
        >
            <div className="container flex items-center justify-between w-full px-2.5 md:px-5">
                <div className="z-50 logo">
                    <div className="js-nav-logo">
                        <LocalizedLink
                            onClickHandler={() => {
                                if (isMenuOpen) {
                                    toggleMenu();
                                }
                            }}
                            label={
                                <img
                                    src="/dotsandlines.svg"
                                    width="48"
                                    height="59"
                                    alt="dotsandlineslogo"
                                />
                            }
                            aria-label="Link to home"
                            slug=""
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-8">
                    {data.contactLabel && (
                        <LocalizedLink
                            onClickHandler={() => {
                                if (isMenuOpen) {
                                    toggleMenu();
                                }
                            }}
                            className="z-50 p-2 lowercase d-body-l md:py-0 md:px-6 hover:underline"
                            slug={`${data.contactData.contactLink?.internal?.metadata?.slug.current}`}
                            label={
                                isMobile ? (
                                    <img
                                        width="24"
                                        height="24"
                                        src="/icon_email.svg"
                                        alt="Email Icon"
                                    />
                                ) : (
                                    data.contactLabel
                                )
                            }
                        />
                    )}
                    <button
                        className="relative z-50 lowercase d-body-l min-w-[63px]"
                        onMouseEnter={() => setCursorState("hover")}
                        onMouseLeave={() => setCursorState("default")}
                        onClick={toggleMenu}
                    >
                        {!isMenuOpen ? "Menu" : "Close"}
                    </button>
                </div>

                <nav
                    ref={menuRef}
                    style={{ "--custom-bg-color": bgColor } as React.CSSProperties}
                    className={`body-scroll-lock-container absolute top-0 bottom-0 left-0 right-0 z-40 hidden w-full menu min-h-dvh bg-[var(--custom-bg-color)] overflow-auto`}
                >
                    <div ref={scrollContainer} className="container relative">
                        <div className="container flex flex-col justify-between w-full h-full max-h-screen ml-4 md:items-center md:justify-start md:flex-row md:ml-0">
                            <div className="flex-grow flex gap-8 mx-4.5 md:mx-5">
                                <div className="items-center hidden max-h-screen md:flex d-responsive-menu-item menu-and">
                                    and
                                </div>
                                <div className="flex items-end pt-20 pb-10 overflow-auto hide-scrollbar md:h-screen md:py-0 md:items-unset ">
                                    <ul
                                        onMouseEnter={() => setCursorState("hover")}
                                        onMouseLeave={() => setCursorState("default")}
                                        className="flex flex-col md:mt-auto md:mb-auto"
                                    >
                                        {data.mainNavigation?.map((item, index) => (
                                            <li key={index} className="inline lowercase">
                                                <div className="block overflow-hidden m-display-1 md:d-responsive-menu-item">
                                                    <div className="menu-item">
                                                        <LocalizedLink
                                                            slug={`${item.metadata?.slug.current}`}
                                                            label={item.label}
                                                            onClickHandler={toggleMenu}
                                                        />
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 xl:gap-8 md:absolute md:bottom-11 md:left-11 mx-4.5 md:mx-0 pb-10 md:pb-0">
                                {data.socialLinks && <SocialLinks socialLinks={data.socialLinks} />}

                                <ContactData contactData={data.contactData} />

                                <div className="flex items-end justify-between">
                                    {data.showLanguageSwitcher && (
                                        <LanguageSwitcher onLanguageChange={toggleMenu} />
                                    )}
                                    {isMobile && data.contactData?.contactLink && (
                                        <ContactLink
                                            onToggleMenu={toggleMenu}
                                            contactData={data.contactData}
                                        />
                                    )}
                                </div>
                            </div>

                            {!isMobile && data.contactData?.contactLink && (
                                <ContactLink
                                    onToggleMenu={toggleMenu}
                                    contactData={data.contactData}
                                />
                            )}
                        </div>
                    </div>
                </nav>
            </div>
        </header>
    );
};

export default Nav;
